import { AppListItem } from "client/Features/Layout/TitleBar/GlobalSearch/Items/AppListItem";
import { SearchListItem } from "client/Features/Search/SearchListItem";
import { generateTrackingId } from "client/Misc/utils";
import { ReactElement, useMemo } from "react";
import { ValidGlobalSearchItemPersistedModel } from "../../Persistence/validation";
import { GlobalSearchResultItemKind, MaintenanceWindowAppModel } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { TimeZone } from "client/DateAndTime/timezones";
import { AppColorViewModel } from "client/Api/Apps/common";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { TagViewModel } from "client/Api/Tags/tags";
import { AppNavigationParameters, useAppNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useAppNavigationHandler";
import {
	convertAppColor,
	convertAppImage,
	convertMaintenanceWindow,
	convertTag,
	tryConvertAppToNavigationParameters
} from "../../Persistence/convertPersistedData";

export type AppItemViewProps = {
	item: ValidGlobalSearchItemPersistedModel<GlobalSearchResultItemKind.App>;
	navigationIndex: number;
	timezone: TimeZone;
	onRemove: () => void;
};

export const AppItemView = (props: AppItemViewProps): ReactElement => {
	const {
		item,
		navigationIndex,
		onRemove,
		timezone
	} = props;

	const {
		id,
		category,
		color: persistedColor,
		image: persistedImage,
		maintenanceWindow: persistedMaintenanceWindow,
		name,
		tags: persistedTags
	} = item.app;

	const color = useMemo(
		(): AppColorViewModel => {
			return convertAppColor(persistedColor);
		},
		[persistedColor]
	);

	const image = useMemo(
		(): AppImageDisplayInfo => {
			return convertAppImage(persistedImage);
		},
		[persistedImage]
	);

	const maintenanceWindow = useMemo(
		(): MaintenanceWindowAppModel | null => {
			if (!persistedMaintenanceWindow) {
				return null;
			}

			return convertMaintenanceWindow(persistedMaintenanceWindow);
		},
		[persistedMaintenanceWindow]
	);

	const tags = useMemo(
		(): readonly TagViewModel[] => {
			return persistedTags.map(t => convertTag(t));
		},
		[persistedTags]
	);

	const appConversion = useMemo(
		(): TryResult<AppNavigationParameters> => {
			return tryConvertAppToNavigationParameters(item.app);
		},
		[item.app]
	);

	const executeHandler: () => void = useAppNavigationHandler({
		id: id,
		maintenanceWindow,
		parameters: appConversion.wasSuccessful
			? appConversion.result
			: "unresolved",
		recentlyOpenedItem: item,
		timezone
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("app-item", `|${id}`)}
		>
			<AppListItem
				name={name}
				category={category}
				color={color}
				image={image}
				id={id}
				maintenanceWindow={maintenanceWindow}
				tags={tags}
				onRemove={onRemove}
				itemIndex={navigationIndex}
				tabIndex={-1}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
AppItemView.displayName = "AppItemView";
